'use client';

import { useLayoutEffect, useRef } from 'react';
import type { Key } from 'react-aria';
import { useStableCallback } from 'hooks/useStableCallback';
import type { Modality } from '@react-aria/interactions';
import { getInteractionModality } from '@react-aria/interactions';
import type { ScrollAlignment, useScrollTo } from './useScrollTo';
import type { useSmoothScroll } from './useSmoothScrollTo';

export type UseScrollKeyIntoViewOptions = {
  getAutoScrollConfig?: (
    key: Key,
    modality: Modality
  ) => {
    alignment: ScrollAlignment | 'auto';
    behavior:
      | {
          type: 'smooth';
          force?: boolean;
          duration?: number;
          ease: (t: number) => number;
        }
      | {
          type: 'immediate';
          force?: boolean;
        };
  };
};

/**
 * Scroll the focused key into view when the user switches focus
 * using the keyboard.
 */

export function useScrollKeyIntoView(
  focusedKey: Key,
  scrollToItem: ReturnType<typeof useScrollTo>,
  smoothScroll: ReturnType<typeof useSmoothScroll>,
  { getAutoScrollConfig }: UseScrollKeyIntoViewOptions = {}
) {
  const lastFocusedKey = useRef<Key | null>(null);

  // ensure the callback is defined before assigning it to a ref
  const getConfigStable = useStableCallback(
    getAutoScrollConfig ?? ((): undefined => undefined)
  );

  useLayoutEffect(() => {
    if (focusedKey !== lastFocusedKey.current) {
      const modality = getInteractionModality();
      if (!modality) return; // no need to scroll if the modality is not defined

      const config = getConfigStable(focusedKey, modality);

      // custom behavior
      if (config) {
        if (config.behavior.type === 'smooth') {
          smoothScroll(focusedKey, config.alignment, {
            force: config.behavior.force,
            duration: config.behavior.duration,
            ease: config.behavior.ease,
          });
        } else {
          scrollToItem(focusedKey, config.alignment, {
            force: config.behavior.force,
          });
        }
        // default behavior
      } else if (modality === 'virtual') {
        smoothScroll(focusedKey, 'auto');
      } else if (modality !== 'pointer') {
        scrollToItem(focusedKey, 'auto');
      }
    }
    lastFocusedKey.current = focusedKey;
  }, [focusedKey, scrollToItem, smoothScroll, getConfigStable]);
}
